// Public case study archive.
//
// Monthly ledger of closed decisions from the live desk, with the audited
// historical record beneath it. Public read-only surface.

import { createFileRoute } from "@tanstack/react-router";
import { CaseStudyLedgerView } from "@/components/archive/CaseStudyLedgerView";
import record from "@/data/track-record.json";
import { AUDIT_METHOD } from "@/components/archive/AuditStandard";

const CANONICAL = "https://xauproacademy.com/case-studies";
const TITLE = "Live XAUUSD Signal Case Studies with Trade Replay | XAU Pro Academy";
const DESCRIPTION =
  "Every closed gold signal from the Orion Live Desk, with entry, stop, targets, result in R and a candle by candle replay. Backed by a 241 position record: +95.94R, profit factor 1.72.";

const o = record.combined.overall;

export const Route = createFileRoute("/case-studies/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "verified gold signals, XAUUSD signals results, gold trade case studies, transparent forex signals, prop firm gold signals, trade replay, reliable trading signals",
      },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: "Live Gold Signal Case Studies with Forensic Replay" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:site_name", content: "XAU Pro Academy" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Live Gold Signal Case Studies with Forensic Replay" },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "XAUUSD Live Desk Case Studies",
              description: DESCRIPTION,
              url: CANONICAL,
              isPartOf: { "@type": "WebSite", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
              about: { "@type": "Thing", name: "XAUUSD gold trading signals" },
            },
            {
              "@type": "Dataset",
              name: "XAUUSD Signals Performance Record 2024 to 2026",
              description: `${o.trades} closed XAUUSD positions, net ${o.netR}R, profit factor ${o.profitFactor}, maximum drawdown ${o.maxDrawdownR}R, win rate ${o.winRate}%.`,
              url: "https://xauproacademy.com/track-record",
              temporalCoverage: "2024-01-01/2026-10-31",
              spatialCoverage: { "@type": "Place", name: "Global spot gold market (XAUUSD)" },
              identifier: "https://xauproacademy.com/track-record#dataset",
              version: "2026.10",
              dateModified: "2026-10-15",
              inLanguage: "en",
              license: "https://xauproacademy.com/terms",
              citation: "XAU Pro Academy. XAUUSD Signals Performance Record 2024 to 2026. https://xauproacademy.com/track-record",
              distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: "https://xauproacademy.com/track-record" }],
              creator: { "@type": "Organization", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
              publisher: { "@type": "Organization", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
              measurementTechnique: AUDIT_METHOD,
              isAccessibleForFree: true,
              keywords: ["XAUUSD", "gold", "case study", "trade replay", "OTAS-01", "OSAS-01"],
              variableMeasured: [
                { "@type": "PropertyValue", name: "Net return", unitText: "R", value: o.netR },
                { "@type": "PropertyValue", name: "Profit factor", value: o.profitFactor },
                { "@type": "PropertyValue", name: "Maximum drawdown", unitText: "R", value: o.maxDrawdownR },
                { "@type": "PropertyValue", name: "Win rate", unitText: "percent", value: o.winRate },
                { "@type": "PropertyValue", name: "Closed positions", value: o.trades },
              ],
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://xauproacademy.com/" },
                { "@type": "ListItem", position: 2, name: "Case Studies", item: CANONICAL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: CaseStudyIndex,
});

function CaseStudyIndex() {
  return <CaseStudyLedgerView />;
}
